import { Button, createStyles, makeStyles, type Theme } from '@material-ui/core';
import type { StandardDivProps } from '@sprinx/react-mui-components';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import SimpleImage from './SimpleImage';

type CarouselItem = {
  alt: string;
  href?: string;
  src: string;
  title?: string;
};

export type CarouselProps = StandardDivProps<CarouselClassNameKey> & {
  interval?: number;
  items: CarouselItem[];
};

export type CarouselClassNameKey =
  | 'root'
  | 'carouselImage'
  | 'carouselImageActive'
  | 'carouselDots'
  | 'dot'
  | 'dotActive'
  | 'imageToDefineCarouselHeight';

const useStyles = makeStyles((theme: Theme) =>
  createStyles<CarouselClassNameKey, {}>({
    root: {
      position: 'relative',
      width: '100%',
      height: 'auto',
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadius,
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
    },
    carouselImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      opacity: 0,
      transition: 'opacity 0.7s ease-in-out',
      pointerEvents: 'none',
    },
    carouselImageActive: {
      opacity: 1,
      pointerEvents: 'auto',
    },
    carouselDots: {
      position: 'absolute',
      bottom: '15px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: '8px',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    dot: {
      minWidth: '10px', // due to default button styles
      height: '10px',
      borderRadius: '50%',
      background: '#ffcd69',
      cursor: 'pointer',
      transition: 'background 0.3s',
      '&:hover': {
        background: 'grey',
      },
    },
    dotActive: {
      background: '#FFA800',
    },
    imageToDefineCarouselHeight: {
      display: 'block',
      width: '100%',
      visibility: 'hidden',
    },
  }),
);

const Carousel: React.FC<CarouselProps> = ({ className, classes: pClasses, items, interval = 7000 }) => {
  const classes = useStyles({ classes: pClasses });
  const t = useTranslateWithFallback();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, interval);
    return () => clearInterval(id);
  }, [items.length, interval]);

  if (items.length === 0) {
    return null;
  }

  const defaultImage = items[0];

  return (
    <div className={clsx(classes.root, className)}>
      <img
        src={defaultImage.src}
        alt={defaultImage.alt || defaultImage.title}
        title={defaultImage.title}
        className={clsx(classes.imageToDefineCarouselHeight)}
      />
      {items.map((item, i) => {
        if (item.href) {
          return (
            <Link to={item.href} key={i}>
              <SimpleImage
                src={item.src}
                alt={item.alt}
                title={item.title || item.alt}
                className={clsx(classes.carouselImage, i === currentIndex && classes.carouselImageActive)}
              />
            </Link>
          );
        }
        return (
          <SimpleImage
            key={i}
            src={item.src}
            alt={item.alt}
            title={item.title || item.alt}
            className={clsx(classes.carouselImage, i === currentIndex && classes.carouselImageActive)}
          />
        );
      })}

      <div className={classes.carouselDots}>
        {items.map((_, i) => (
          <Button
            component='span'
            key={i}
            className={clsx(classes.dot, i === currentIndex && classes.dotActive)}
            onClick={() => setCurrentIndex(i)}
            aria-label={t('homePage/carousel/showItem', { number: i + 1 })}
          ></Button>
        ))}
      </div>
    </div>
  );
};

Carousel.displayName = 'Carousel';

export default Carousel;
